import { Fragment, useState } from 'react';

import type { Language } from '@dec/shared';
import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { HookForm, Translable } from '@dec/shared';
import { BtnGroup, BtnToolbar, Col, Row } from '@dec/shared';
import { Check, Combobox, Number, Text } from '@dec/shared';
import { useTranslateList } from 'app/components/translate/useTranslateList';
import { ConfirmDialog } from 'app/core/dialog';
import { factoryYup, limit } from 'app/core/factory-yup';
import { notificationSuccess } from 'app/core/toast';
import { useFormCellTypes } from 'app/modules/TaxStatement/FormCells/hooks';
import Env from 'env';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCatalogList } from '@/app/modules/configuration/catalogs/hooks/useCatalogList';

import { label as labelCommon } from '../../../../../translation';
import { createReportCell, deleteReportCell, updateReportCell } from '../http';
import { messages } from '../translation';
import { label } from '../translation';
import { type ReportCell } from '../types';

const yup = factoryYup();
interface Props {
  defaultValue: ReportCell;
  idVersion: string;
}

const fielddRequired = 'common.validations.fieldRequired';

const validations = yup.object().shape({
  name: yup.string().required(i18next.t(fielddRequired)).max(limit.maxName),
  description: yup.string().required(i18next.t(fielddRequired)).max(limit.maxName),
  code: yup.number().nullable().required(i18next.t(fielddRequired)).min(1),
  cellType: yup.object().required(i18next.t(fielddRequired)),
  formula: yup.string().nullable().max(255),
  order: yup.number().nullable().min(1)
});

interface ReportCellRequest extends ReportCell {
  reportVersionId?: string;
  descriptionTranslate: Array<Language>;
  nameTranslate: Array<Language>;
}

const ReportCellForm = (props: Props) => {
  const availableLanguages = useTranslateList();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();
  const cellTypes = useFormCellTypes();
  const { isLoading: loading, data: catalogTypes } = useCatalogList();
  const navigate = useNavigate();
  const { defaultValue } = props;

  const [type, setType] = useState(defaultValue?.type);

  const [nameTranslate] = defaultValue.translations?.filter((d) => d.key === 'name') || [];
  const [descriptionTranslate] =
    defaultValue.translations?.filter((d) => d.key === 'description') || [];

  const initialValue: ReportCellRequest = {
    ...defaultValue,
    nameTranslate: nameTranslate?.languages,
    descriptionTranslate: descriptionTranslate?.languages
  };

  const save = async (request: ReportCellRequest) => {
    request.id = defaultValue?.id;
    request.reportVersionId = props.idVersion;
    request.type = request.cellType.id;
    request.catalogTypeId = request.catalogType?.id;

    const { nameTranslate, descriptionTranslate, ...reportCelll } = request;
    const payload: ReportCell = {
      ...reportCelll,
      translations: [
        { key: 'name', languages: nameTranslate },
        { key: 'description', languages: descriptionTranslate }
      ]
    };
    try {
      if (payload.id) {
        await updateReportCell(payload.id, payload);
      } else {
        await createReportCell(payload);
      }
      notificationSuccess(i18next.t('taxes.messages.savedSuccessfully'));
      navigate(`/taxes/manage/complementary/versions/${props.idVersion}/cells`);
    } catch (e) {
      console.error(e);
    }
  };

  const onCancel = () => {
    navigate(`/taxes/manage/complementary/versions/${props.idVersion}/cells`);
  };
  if (loading) {
    return <></>;
  }
  return (
    <Fragment>
      <HookForm initialValues={initialValue} validations={validations} onSubmit={save}>
        <Row>
          <Col>
            <Number label={label(t, 'code')} name={'code'} />
          </Col>
          <Col>
            <Text label={label(t, 'name')} name={'name'} />
          </Col>
        </Row>
        <Row>
          <Col>
            <Text label={label(t, 'description')} name={'description'} />
            <Translable
              propertyTranslate="description"
              name="descriptionTranslate"
              defaultValues={initialValue.descriptionTranslate}
              availableLanguages={availableLanguages}
              defaultLang={Env.defaultLang}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <Combobox
              label={label(t, 'type')}
              name="cellType"
              display="name"
              code="id"
              data={cellTypes}
              whenChange={(val: any) => {
                setType(val?.id);
              }}
            />
          </Col>
        </Row>
        {catalogTypes && type === 'list' && (
          <Row>
            <Col>
              <Combobox
                label={label(t, 'catalogType')}
                name="catalogType"
                display="name"
                code="id"
                data={catalogTypes}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <Number label={label(t, 'order')} name={'order'} />
          </Col>
          <Col>
            <Text label={label(t, 'formula')} name={'formula'} />
          </Col>
        </Row>

        <Row>
          <Col>
            <Check label={label(t, 'mandatory')} name={'mandatory'} />
          </Col>
        </Row>

        <BtnToolbar>
          <BtnGroup>
            <Button action={ButtonAction.cancel} variant="secondary" onClick={onCancel} />

            <Button
              visible={initialValue?.id !== undefined}
              action={ButtonAction.delete}
              variant={'danger'}
              onClick={async () => {
                const ok = await ConfirmDialog({
                  titleModal: messages(t, 'confirmTitle'),
                  message: messages(t, 'confirmDeleted'),
                  okButton: labelCommon('ok'),
                  cancelButton: labelCommon('cancel')
                });
                if (ok) {
                  try {
                    await deleteReportCell(defaultValue.id as string);
                    notificationSuccess(i18next.t('taxes.messages.deletedSuccessfully'));
                    navigate(`/taxes/manage/complementary/versions/${props.idVersion}/cells`);
                  } catch (e) {
                    console.error(e);
                  }
                }
              }}
            />
          </BtnGroup>
          <BtnGroup>
            <Button action={ButtonAction.save} variant="primary" type={'submit'} />
          </BtnGroup>
        </BtnToolbar>
      </HookForm>
    </Fragment>
  );
};
export default ReportCellForm;
